import React, { useMemo } from "react";

import Modal from "../../Modal";
import { useDateTimeFormat } from "../../../hooks/useDateTimeFormat";
import {
  formatQuestionnaireTypeFallback,
  getQuestionnaireScoreParts,
  getQuestionnaireTypeDefinition,
} from "../../../utils/patientProfileData";
import {
  buildQuestionnaireReviewFormData,
  formatQuestionnaireReviewAnswer,
  formatQuestionnaireReviewLabel,
  isQuestionnaireReviewAuxiliaryField,
} from "../../../utils/questionnaireReviewPresentation";
import EnvironmentalContextSection from "../../results/patientRecordingOverlay/EnvironmentalContextSection";

function normalizeOptionalString(value) {
  if (value === undefined || value === null) {
    return null;
  }

  const normalizedValue = String(value).trim();
  return normalizedValue.length > 0 ? normalizedValue : null;
}

function humanizeToken(value) {
  const normalizedValue = normalizeOptionalString(value);

  if (!normalizedValue) {
    return "Unknown";
  }

  return normalizedValue
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./u, (character) => character.toUpperCase());
}

function buildReviewItems(entry, questionnaireType) {
  const snapshotItems = Array.isArray(entry?.reviewSnapshot?.items)
    ? entry.reviewSnapshot.items
    : [];

  if (snapshotItems.length > 0) {
    return snapshotItems
      .map((item) => ({
        id: normalizeOptionalString(item?.id) || item?.label || "question",
        label: normalizeOptionalString(item?.label) || "Question",
        answerText: normalizeOptionalString(item?.answerText) || "N/A",
      }))
      .filter(Boolean);
  }

  const responseItems = Array.isArray(entry?.responseItems) ? entry.responseItems : [];
  const formData = buildQuestionnaireReviewFormData(entry);

  if (responseItems.length > 0) {
    return responseItems
      .filter((item) => !isQuestionnaireReviewAuxiliaryField(item?.key))
      .map((item) => ({
        id: normalizeOptionalString(item?.key) || "question",
        label: formatQuestionnaireReviewLabel(questionnaireType, item?.key),
        answerText:
          normalizeOptionalString(item?.answerText) ||
          formatQuestionnaireReviewAnswer(
            questionnaireType,
            item?.key,
            formData,
          ),
      }));
  }

  const rawResponses =
    formData && typeof formData === "object" ? formData : {};

  return Object.entries(rawResponses)
    .filter(([key]) => !isQuestionnaireReviewAuxiliaryField(key))
    .map(([key]) => ({
      id: key,
      label: formatQuestionnaireReviewLabel(questionnaireType, key),
      answerText: formatQuestionnaireReviewAnswer(
        questionnaireType,
        key,
        formData,
      ),
    }));
}

function DetailRow({ label, value }) {
  if (!value) {
    return null;
  }

  return (
    <div className="flex items-start justify-between gap-3 border-b border-greyscale-1 py-2">
      <span className="text-sm font-medium text-greyscale-3">{label}</span>
      <span className="text-right text-sm text-typography">{value}</span>
    </div>
  );
}

function SummaryCard({ label, value, quietValue = null }) {
  return (
    <div className="card px-4 py-3">
      <p className="text-sm text-greyscale-3">{label}</p>
      <div className="flex items-end gap-2">
        <p className="text-2xl font-semibold text-typography">{value}</p>
        {quietValue ? (
          <p className="pb-1 text-sm font-medium text-greyscale-3">{quietValue}</p>
        ) : null}
      </div>
    </div>
  );
}

export default function PatientQuestionnaireOverlay({
  entry,
  open,
  onClose,
  showWeatherDetails = true,
  t,
}) {
  const { formatDateTime } = useDateTimeFormat();
  const questionnaireType = entry?.questionnaireType || entry?.questionnaireId;
  const definition = getQuestionnaireTypeDefinition(questionnaireType);
  const questionnaireLabel = definition
    ? t(definition.labelKey, definition.labelDefault)
    : formatQuestionnaireTypeFallback(questionnaireType);
  const scoreParts = getQuestionnaireScoreParts(
    entry?.score,
    questionnaireType,
    entry,
  );
  const reviewItems = useMemo(
    () => buildReviewItems(entry, questionnaireType),
    [entry, questionnaireType],
  );
  const submissionProvenance = entry?.submissionProvenance || {};
  const completedAt =
    submissionProvenance.completedAt ||
    entry?.takenAt ||
    entry?.submittedAt ||
    entry?.clientSubmittedAt ||
    null;
  const completedDateTime = formatDateTime(completedAt);

  return (
    <Modal
      open={open && Boolean(entry)}
      onClose={onClose}
      panelClassName="max-w-3xl w-full max-h-[85vh] overflow-y-auto"
    >
      {entry ? (
        <>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-greyscale-3">
                {t("profilePage.questionnaireDetailsTitle", "Questionnaire")}
              </p>
              <h2 className="text-2xl font-semibold text-typography">
                {entry?.reviewSnapshot?.questionnaireTitle || questionnaireLabel}
              </h2>
              <p className="mt-1 text-sm text-greyscale-3">
                {[completedDateTime.date, completedDateTime.time]
                  .filter(Boolean)
                  .join(" ")}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-greyscale-1 text-typography transition-colors hover:bg-greyscale-0.5"
              aria-label={t("profilePage.close", "Close")}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                close
              </span>
            </button>
          </div>

          {scoreParts ? (
            <SummaryCard
              label={t("profilePage.scoreHeader", "Score")}
              value={String(scoreParts.score)}
              quietValue={
                scoreParts.maxScore !== null ? `/ ${scoreParts.maxScore}` : null
              }
            />
          ) : null}

          <section className="card px-4 py-3">
            <h3 className="text-base font-semibold text-typography">
              {t("profilePage.submissionDetailsTitle", "Submission details")}
            </h3>
            <div className="mt-2">
              <DetailRow
                label={t("profilePage.questionnaireTypeHeader", "Type")}
                value={questionnaireLabel}
              />
              <DetailRow
                label={t("profilePage.completedAtLabel", "Completed")}
                value={[completedDateTime.date, completedDateTime.time]
                  .filter(Boolean)
                  .join(" ")}
              />
              <DetailRow
                label={t("profilePage.versionLabel", "Version")}
                value={
                  submissionProvenance.questionnaireVersion ||
                  entry?.questionnaireVersion ||
                  entry?.contentVersion
                }
              />
              <DetailRow
                label={t("profilePage.respondentLabel", "Respondent")}
                value={humanizeToken(submissionProvenance.respondentType)}
              />
              <DetailRow
                label={t("profilePage.completionMethodLabel", "Completion method")}
                value={humanizeToken(submissionProvenance.completionMethod)}
              />
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <h3 className="text-base font-semibold text-typography">
              {t("profilePage.responsesTitle", "Responses")}
            </h3>

            {reviewItems.length > 0 ? (
              reviewItems.map((item) => (
                <article key={item.id} className="card px-4 py-3">
                  <h4 className="text-sm font-semibold text-typography">
                    {item.label}
                  </h4>
                  <div className="mt-2 flex flex-col gap-1">
                    {String(item.answerText || "N/A")
                      .split("\n")
                      .filter(Boolean)
                      .map((line, index) => (
                        <p
                          key={`${item.id}-${index}`}
                          className="whitespace-pre-line text-sm text-typography"
                        >
                          {line}
                        </p>
                      ))}
                  </div>
                </article>
              ))
            ) : (
              <div className="card px-4 py-4 text-sm text-greyscale-3">
                {t(
                  "profilePage.noQuestionnaireResponses",
                  "No response breakdown is available for this questionnaire.",
                )}
              </div>
            )}
          </section>

          {showWeatherDetails && entry?.environmentalContext ? (
            <section className="card px-4 py-3">
              <h3 className="text-base font-semibold text-typography">
                {t("profilePage.environmentHeader", "Environment")}
              </h3>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                <EnvironmentalContextSection
                  context={entry.environmentalContext}
                  t={t}
                />
              </div>
            </section>
          ) : null}
        </>
      ) : null}
    </Modal>
  );
}
